// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { kindOf } from '../../app/keyTargets';
import { useEditorCursor, useEditorGesture, useGeometriEditor, type GeometriEditor, type SnapToggles } from '../../app/bygning/GeometriEditorContext';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';
import { modeTabByPath } from '../../bygning/modes';
import {
  IMPORT_SOURCES,
  defaultRedigerTab,
  importHistory,
  parseImportSource,
  redigerHref,
  type ImportSource,
  type RedigerTab,
} from '../../bygning/rediger';
import {
  elementBox,
  elementStorey,
  elementTree,
  findElement,
  geometryWarnings,
  type GeoWarning,
  type GeometriModel,
} from '../../bygning/geometri';
import { deleteOps, editLabel, fieldOps, propertiesView, resetFieldOps, revertOps, type FieldKey } from '../../bygning/geometriEdit';
import { CODE_WAIT_MS, flushKeys, resolveKeys } from '../../bygning/geometriKeys';
import { saveState } from '../../bygning/geometriSync';
import type { Selection } from '../../bygning/selection';
import { formatDateTime } from '../../deling/model';
import { Checkbox } from '../../components/Checkbox';
import { ElementTree } from '../../components/bygning/rediger/ElementTree';
import { SelectionBar, StatusLine, StoreyChip, ToolDock, ViewCluster } from '../../components/bygning/rediger/GeometriChrome';
import { PropertiesPanel } from '../../components/bygning/rediger/PropertiesPanel';
import { useSettings } from '../../components/bygning/settings/SettingsContext';
import type { ModelSettingsState, SettingsAction } from '../../components/bygning/settings/settingsState';
import { EmptyState } from '../../components/EmptyState';
import { Pill } from '../../components/Pill';
import { Spinner } from '../../components/Spinner';
import { BimImportDialog } from '../../components/bygning/BimImportDialog';
import { RtabmapImportDialog } from '../../components/posegraf/RtabmapImportDialog';
import { GraphViewPage } from '../GraphViewPage';
import type { ModeComponent, ModeProps, ModeView } from './modeTypes';
import styles from './RedigerMode.module.css';

/** A count the server may not know (an older ruxd without the route): null. */
function countOrNull<T>(run: (signal: AbortSignal) => Promise<T[]>): (signal: AbortSignal) => Promise<number | null> {
  return async (signal) => {
    try {
      return (await run(signal)).length;
    } catch (cause) {
      if (signal.aborted) throw cause;
      return null;
    }
  };
}

/**
 * `/rediger` with no sub-tab: picks Geometri, Scan & pose or Import from
 * what the case holds (`defaultRedigerTab`) and replaces the URL with it,
 * keeping the query.
 */
function useDefaultTab(active: boolean): void {
  const navigate = useNavigate();
  const { search } = useLocation();
  const walls = useAsync(countOrNull((s) => (active ? api.walls(s) : Promise.resolve([]))), [active]);
  const spaces = useAsync(countOrNull((s) => (active ? api.spaces(s) : Promise.resolve([]))), [active]);
  const frames = useAsync(
    async (signal) => {
      if (!active) return 0;
      try {
        return (await api.projectSummary(signal)).sensor_frames.total_count;
      } catch (cause) {
        if (signal.aborted) throw cause;
        return null;
      }
    },
    [active],
  );
  const tab = defaultRedigerTab({ walls: walls.data, spaces: spaces.data, sensorFrames: frames.data });
  useEffect(() => {
    if (active && tab) navigate(`${redigerHref(tab)}${search}`, { replace: true });
  }, [active, tab, navigate, search]);
}

/**
 * Rediger (frontend-u0o.1): the editing mode, for editors and owners — the
 * tab is hidden from a viewer, whose deep link lands read-only (the shell's
 * Læseadgang notice; every write control checks `useCanEdit()` itself).
 * Three sub-tabs, nested routes the shell draws as a bar under its top bar,
 * in the order the work goes:
 *
 *  - Import (`/rediger/import?source=`): every source that writes geometry
 *    — RTAB-Map sessions, Speckle, IFC — as one page: the sources left, the
 *    chosen source's form in the centre, the import history right.
 *  - Scan & pose (`/rediger/scan`): the pose-graph and scan-repair editor
 *    (the old Posegraf, `/graph-view`), laid over the Ground as a cover —
 *    it has its own expert canvas.
 *  - Geometri (`/rediger/geometri`): the building model (walls, openings,
 *    spaces, roof) edited over the Ground's plan or 3D model as in a CAD
 *    program (frontend-u0o.9; `useGeometriView`).
 */
export const RedigerMode: ModeComponent = ({ data, sel, setSel }) => {
  const location = useLocation();
  const tab = (modeTabByPath(location.pathname)?.tab ?? null) as RedigerTab | null;
  useDefaultTab(tab === null);
  const { state: settings, dispatch } = useSettings();
  const geometri = useGeometriView({ sel, setSel, settings, dispatch });

  if (tab === 'scan') return { cover: <GraphViewPage /> };
  if (tab === 'import') return importView(parseImportSource(location.search), data.canEdit);
  if (tab === 'geometri') return geometri;
  return {
    centre: (
      <div className={styles.waiting}>
        <Spinner label="Åbner Rediger…" />
      </div>
    ),
  };
};

// ------------------------------------------------------------ geometri --

/** The history label of a field edit: "Tykkelse · Væg 6". */
const FIELD_LABEL: Record<FieldKey, string> = {
  length: 'Længde',
  thickness: 'Tykkelse',
  height: 'Højde',
  base: 'Underkant',
  external: 'Type',
  width: 'Bredde',
  opening_height: 'Højde',
  sill: 'Brystning',
  opening_kind: 'Art',
  name: 'Navn',
  space_kind: 'Type',
  floor: 'Gulv',
  ceiling_height: 'Loftshøjde',
  area: 'Areal',
};

/** How many history rows the browser shows before "Vis alle". */
const HISTORY_ROWS = 5;

interface GeometriProps {
  sel: Selection;
  setSel: ModeProps['setSel'];
  settings: ModelSettingsState;
  dispatch: (action: SettingsAction) => void;
}

/**
 * Rediger › Geometri as a CAD editor (frontend-u0o.9, design.md §3): the
 * plan (or 3D) is the page; around it only what the moment needs — the
 * browser left (Etager with their elements, Historik), the selection bar
 * top centre and Egenskaber right while something is selected, the view
 * cluster top right, the tool dock bottom centre and the status line along
 * the bottom; the storey chip sits in the sub-tab bar. The model, the
 * selection and the writes are the shared editor's
 * (`GeometriEditorContext`); this lays them out and takes the keys.
 */
function useGeometriView({ sel, setSel, settings, dispatch }: GeometriProps): ModeView {
  const editor = useGeometriEditor();
  const model = editor?.model ?? EMPTY_MODEL;
  const selection = editor?.selection ?? NO_SELECTION;
  const warnings = useMemo(() => geometryWarnings(model), [model]);
  const tree = useMemo(() => elementTree(model, { uncertainOnly: sel.uncertain, warnings }), [model, sel.uncertain, warnings]);
  const storeyFilter = settings.buildingElements.storey;
  const [allHistory, setAllHistory] = useState(false);

  const storeyOf = useCallback(
    (guid: string) => {
      const found = findElement(model, guid);
      return found ? elementStorey(model, found) : null;
    },
    [model],
  );
  const shownWarnings = useMemo(
    () => (storeyFilter.storeyId === null ? warnings : warnings.filter((w) => w.guids.some((g) => storeyOf(g) === storeyFilter.storeyId))),
    [warnings, storeyFilter.storeyId, storeyOf],
  );

  const select = useCallback(
    (guid: string) => {
      editor?.select([guid]);
      // A storey shown alone hides the others: follow the element to its own.
      const sid = storeyOf(guid);
      if (storeyFilter.storeyId !== null && sid !== null && sid !== storeyFilter.storeyId) {
        dispatch({ type: 'buildingElementStorey', storey: { ...storeyFilter, storeyId: sid } });
      }
    },
    [editor, storeyOf, storeyFilter, dispatch],
  );
  const zoom = useCallback(
    (guid: string) => {
      const box = elementBox(model, guid);
      if (box) dispatch({ type: 'focus', box });
    },
    [model, dispatch],
  );

  useGeometriKeys(editor);

  // The editor works on one storey's plan: with none picked, open on the
  // ground floor (the storey whose floor is nearest ±0), once per visit.
  const picked = useRef(false);
  const storeyList = model.storeys;
  useEffect(() => {
    if (picked.current || !editor?.active || !storeyList || storeyList.length === 0) return;
    picked.current = true;
    if (storeyFilter.storeyId !== null) return;
    const ground = [...storeyList].sort((a, b) => Math.abs(a.z_min) - Math.abs(b.z_min) || a.id - b.id)[0];
    dispatch({ type: 'buildingElementStorey', storey: { storeyId: ground.id, andBelow: false } });
  }, [editor?.active, storeyList, storeyFilter.storeyId, dispatch]);

  const primary = selection[0] ?? null;
  const props = useMemo(() => propertiesView(model, primary), [model, primary]);
  const loading = editor?.loading ?? true;
  const empty = !loading && (model.walls ?? []).length + (model.openings ?? []).length + (model.spaces ?? []).length + (model.slabs ?? []).length === 0;
  const uncertainCount = useMemo(() => elementTree(model, { uncertainOnly: true }).reduce((n, s) => n + s.count, 0), [model]);
  if (!editor) return {};

  const commitField = (key: FieldKey, value: number | string) => {
    if (!primary) return;
    const ops = fieldOps(model, primary, key, value);
    if (ops) void editor.commit(ops, editLabel(FIELD_LABEL[key], model, [primary]));
  };
  const resetField = (key: FieldKey) => {
    if (!primary) return;
    const ops = resetFieldOps(model, primary, key);
    if (ops) void editor.commit(ops, editLabel(`${FIELD_LABEL[key]} tilbage`, model, [primary]));
  };

  const history = editor.history;
  const left = (
    <div className={styles.browser}>
      <div className={styles.treeHead}>
        <h2 className={styles.caption}>Etager</h2>
        <Checkbox
          label="Kun usikre"
          hint={uncertainCount.toLocaleString('da-DK')}
          checked={sel.uncertain}
          onChange={(e) => setSel({ uncertain: e.target.checked })}
        />
      </div>
      {loading && tree.length === 0 ? (
        <Spinner label="Indlæser bygningsmodellen…" />
      ) : empty ? (
        <EmptyState
          bare
          title="Ingen bygningsmodel endnu"
          detail="Kør Etager, Vægge og Rum i pipelinen, eller importér en BIM-model."
          action={<Link to={redigerHref('import', { source: 'speckle' })}>Importér BIM-model</Link>}
        />
      ) : tree.length === 0 ? (
        <p className={styles.note}>Ingen usikre elementer.</p>
      ) : (
        <ElementTree
          compact
          tree={tree}
          selected={primary}
          selectedAll={selection}
          onSelect={select}
          storey={storeyFilter.storeyId}
          onStorey={(id) => dispatch({ type: 'buildingElementStorey', storey: { storeyId: id, andBelow: false } })}
        />
      )}
      {history !== null && (
        <section className={styles.historySection} aria-labelledby="geo-history">
          <div className={styles.treeHead}>
            <h2 id="geo-history" className={styles.caption}>
              Historik
            </h2>
            {history.length > HISTORY_ROWS && (
              <button type="button" className={styles.linkButton} onClick={() => setAllHistory((a) => !a)}>
                {allHistory ? 'Vis færre' : 'Vis alle'}
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className={styles.note}>Ingen rettelser endnu.</p>
          ) : (
            <ol className={styles.editList}>
              {(allHistory ? history : history.slice(0, HISTORY_ROWS)).map((e) => (
                <li key={e.id} className={e.undone ? `${styles.editRow} ${styles.editUndone}` : styles.editRow} title={e.undone ? 'Fortrudt' : undefined}>
                  <span className={styles.editLabel}>{e.label || 'Rettelse'}</span>
                  <time className={styles.editWhen} dateTime={e.created_at}>
                    {relativeTime(e.created_at)}
                  </time>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </div>
  );

  const selectionTitle = selection.length === 1 ? (props?.title ?? '1 element') : `${selection.length} elementer`;
  const revertable = revertOps(model, selection).length > 0;
  const deletable = deleteOps(model, selection).length > 0;
  const storeys = (model.storeys ?? []).map((s) => ({ id: s.id, name: s.name, z: s.z_min })).sort((a, b) => b.id - a.id);
  const underlay = settings.clouds.cloud !== false;
  const planView = settings.planLock;

  const overlay = (
    <>
      {selection.length > 0 && (
        <SelectionBar title={selectionTitle} count={selection.length} revertable={revertable} deletable={deletable} onCommand={editor.run} />
      )}
      <ViewCluster
        view={planView ? 'plan' : '3d'}
        onView={(v) => editor.run(v === 'plan' ? 'view:plan' : 'view:3d')}
        underlay={underlay}
        onUnderlay={() => dispatch({ type: 'cloud', name: 'cloud', on: !underlay })}
        onFit={() => editor.run('zoom-fit')}
      />
      {props && (
        <PropertiesPanel
          view={props}
          more={selection.length - 1}
          onField={commitField}
          onReset={resetField}
          onRevert={() => editor.run('revert')}
          onDelete={() => editor.run('delete')}
          onSelect={select}
          onZoom={elementBox(model, props.guid) ? () => zoom(props.guid) : null}
        />
      )}
      <ToolDock tool={editor.tool} onTool={editor.setTool} />
      <EditorStatus
        editor={editor}
        warnings={shownWarnings}
        onPickWarning={(w) => {
          select(w.guids[0]);
          zoom(w.guids[0]);
        }}
      />
    </>
  );
  const subbar = (
    <StoreyChip
      storeys={storeys}
      value={storeyFilter.storeyId}
      onChange={(id) => dispatch({ type: 'buildingElementStorey', storey: { storeyId: id, andBelow: false } })}
    />
  );
  return { left, overlay, subbar };
}

const EMPTY_MODEL: GeometriModel = { storeys: null, walls: null, openings: null, spaces: null, slabs: null };
const NO_SELECTION: readonly string[] = [];

const SNAP_TOGGLES: { key: keyof SnapToggles; label: string; title: string }[] = [
  { key: 'snap', label: 'Snap', title: 'Fang til endepunkter, midtpunkter og kanter (Alt slår det fra under et træk)' },
  { key: 'ortho', label: 'Ortogonal', title: 'Lås til bygningens akser' },
  { key: 'trace', label: 'Planspor', title: 'Fang til de scannede planers spor' },
  { key: 'grid', label: 'Gitter', title: 'Fang til et gitter på 5 mm' },
];

/** The status line, reading the cursor itself so a pointer move redraws only it. */
function EditorStatus({ editor, warnings, onPickWarning }: { editor: GeometriEditor; warnings: readonly GeoWarning[]; onPickWarning: (w: GeoWarning) => void }) {
  const cursor = useEditorCursor(editor);
  const gesture = useEditorGesture(editor);
  const rev = editor.revision;
  return (
    <StatusLine
      tool={editor.tool}
      selected={editor.selection.length}
      hint={gesture?.hint ?? null}
      readout={gesture?.readout ?? null}
      toggles={SNAP_TOGGLES.map((t) => ({ ...t, on: editor.snap[t.key] }))}
      onToggle={(key) => editor.toggleSnap(key as keyof SnapToggles)}
      cursor={cursor}
      warnings={warnings}
      onPickWarning={onPickWarning}
      saved={saveState(rev?.revision ?? null, editor.pending)}
      canUndo={rev?.can_undo ?? false}
      canRedo={rev?.can_redo ?? false}
      onUndo={() => editor.run('undo')}
      onRedo={() => editor.run('redo')}
      stale={rev?.solids_stale ?? false}
      rederiving={editor.rederiving}
      onRederive={() => editor.run('rederive')}
    />
  );
}

/**
 * The editor's keys while Geometri is shown (`geometriKeys.ts`): tools,
 * the two-letter codes, Del, ⌘Z / ⇧⌘Z, Tab and Esc. Captured ahead of the
 * shell's own keys, so Esc first clears the selection; a field, a dialog
 * and the palette keep theirs.
 */
function useGeometriKeys(editor: GeometriEditor | null): void {
  const live = useRef(editor);
  live.current = editor;
  useEffect(() => {
    if (!editor?.active) return;
    let buffer = '';
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      const cmd = flushKeys(buffer);
      buffer = '';
      if (cmd) live.current?.run(cmd);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const ed = live.current;
      if (!ed || e.defaultPrevented) return;
      // A grip drag or a typed dimension owns the keys while it lasts.
      if (ed.gesture.get() !== null) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('[role="dialog"], [role="menu"]')) return;
      const out = resolveKeys(
        { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) },
        buffer,
      );
      if (timer !== null) clearTimeout(timer);
      timer = null;
      buffer = out.buffer;
      if (buffer !== '') timer = setTimeout(flush, CODE_WAIT_MS);
      let consumed = out.consumed;
      for (const cmd of out.commands) {
        // Esc with nothing selected is the shell's (it steps out of the storey focus).
        if (cmd === 'escape' && ed.selection.length === 0) continue;
        // Tab with nothing under the last click keeps its focus move.
        if ((cmd === 'cycle' || cmd === 'cycle-back') && ed.selection.length === 0) {
          consumed = false;
          continue;
        }
        consumed = true;
        ed.run(cmd);
      }
      if (consumed) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      if (timer !== null) clearTimeout(timer);
    };
  }, [editor?.active]);
}

/** "nu", "4 min", "2 t", "3. nov." */
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'nu';
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} t`;
  return new Date(t).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
}

// -------------------------------------------------------------- import --

function importView(source: ImportSource, canEdit: boolean): ModeView {
  return {
    leftWithCentre: true,
    left: <ImportSources current={source} />,
    centre: <ImportForms current={source} canEdit={canEdit} />,
    right: <ImportHistory />,
    rightKey: 'import',
  };
}

/** The source list: one link per source (`?source=`). */
function ImportSources({ current }: { current: ImportSource }) {
  const { search } = useLocation();
  return (
    <nav className={styles.sources} aria-label="Importkilder">
      <h2 className={styles.caption}>Kilder</h2>
      <ul className={styles.sourceList}>
        {IMPORT_SOURCES.map((s) => {
          const q = new URLSearchParams(search);
          q.set('source', s.id);
          const on = s.id === current;
          return (
            <li key={s.id}>
              <Link
                className={on ? `${styles.source} ${styles.sourceOn}` : styles.source}
                to={{ search: `?${q.toString()}` }}
                replace
                aria-current={on ? 'page' : undefined}
              >
                <span className={styles.sourceLabel}>{s.label}</span>
                <span className={styles.sourceHint}>{s.hint}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className={styles.note}>E57 og PLY kommer senere.</p>
    </nav>
  );
}

/**
 * The three forms, all mounted (the inactive ones hidden), so a source's
 * upload keeps running and its form keeps its state while another source is
 * looked at. They are the old dialogs' bodies, rendered inline.
 */
function ImportForms({ current, canEdit }: { current: ImportSource; canEdit: boolean }) {
  const navigate = useNavigate();
  if (!canEdit) {
    return (
      <div className={styles.form}>
        <EmptyState title="Import kræver redigeringsadgang" detail="Bed sagens ejer om rollen redaktør for at importere scanninger eller BIM-modeller." />
      </div>
    );
  }
  return (
    <div className={styles.form}>
      <div hidden={current !== 'rtabmap'}>
        <RtabmapImportDialog
          inline
          onClose={() => undefined}
          onImported={() => undefined}
          onShow={() => navigate(redigerHref('scan', { tilstand: 'blokke' }))}
        />
      </div>
      <div hidden={current !== 'speckle'}>
        <BimImportDialog inline initialTab="speckle" onClose={() => undefined} />
      </div>
      <div hidden={current !== 'ifc'}>
        <BimImportDialog inline initialTab="ifc" onClose={() => undefined} />
      </div>
    </div>
  );
}

/**
 * Importhistorik: the import jobs the event stream holds (newest first),
 * with what each wrote. The server keeps no import ledger yet, so no undo
 * per import (ruxd-5uv.5).
 */
function ImportHistory() {
  const { jobs } = useJobs();
  const rows = useMemo(() => importHistory(jobs), [jobs]);
  return (
    <section className={styles.history} aria-labelledby="rediger-history">
      <h2 id="rediger-history" className={styles.caption}>
        Importhistorik
      </h2>
      {rows.length === 0 ? (
        <EmptyState bare title="Ingen importer endnu" detail="Importer startet i denne sag vises her, med hvad de skrev." />
      ) : (
        <ol className={styles.historyList}>
          {rows.map((r) => (
            <li key={r.id} className={styles.historyRow}>
              <div className={styles.historyHead}>
                <span className={styles.historySource}>{r.sourceLabel}</span>
                <Pill tone={r.tone}>{r.statusLabel}</Pill>
              </div>
              {r.name && <span className={styles.historyName}>{r.name}</span>}
              {r.written && <span className={styles.historyWritten}>{r.written}</span>}
              <time className={styles.historyWhen} dateTime={r.when}>
                {formatDateTime(r.when)}
              </time>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
